import { useEffect, useMemo, useState } from 'react';
import Icon from './Icon';
import { getAccessToken, hasActionPermission, isSecretaryContextUser } from '../authStorage';
import { fetchUnifiedMatrix, resetUnifiedMatrix, saveUnifiedMatrix } from '../unifiedMatrixApi';
import { useLanguage } from '../i18n/language';
import { useT } from '../i18n/translations';
import {
  COMMITTEES_POWERS,
  GENERAL_PROVISIONS,
  GOVERNANCE_LEVELS,
  GOVERNING_PRINCIPLES,
  MATRIX_AXES,
  REGULATING_PRINCIPLES,
  ROLE_DEFINITIONS,
} from '../data/unifiedMatrixData';
import './BranchesPage.css';
import './BoardPage.css';
import './UnifiedMatrixPage.css';

const TABS = [
  { id: 'matrix', label: 'المصفوفة الموحدة', icon: 'report' },
  { id: 'committees', label: 'صلاحيات اللجان', icon: 'board' },
  { id: 'principles', label: 'المبادئ والمستويات', icon: 'lock' },
  { id: 'provisions', label: 'أحكام تنظيمية', icon: 'settings' },
];

const AXIS_ICONS = ['report', 'lock', 'user', 'dashboard', 'money', 'money', 'branch'];

function defaultContent() {
  return JSON.parse(JSON.stringify({
    axes: MATRIX_AXES,
    committees: COMMITTEES_POWERS,
    governingPrinciples: GOVERNING_PRINCIPLES,
    governanceLevels: GOVERNANCE_LEVELS,
    roleDefinitions: ROLE_DEFINITIONS,
    regulatingPrinciples: REGULATING_PRINCIPLES,
    generalProvisions: GENERAL_PROVISIONS,
  }));
}

function roleBadgeClass(value) {
  const v = value || '';
  if (v === '—' || v.includes('غير مخول')) return 'um-badge um-badge--none';
  if (v.includes('اعتماد')) return 'um-badge um-badge--approve';
  if (v.includes('إعداد') || v.includes('تنفيذ') || v.includes('تحضير') || v.includes('تجهيز') || v.includes('اقتراح') || v.includes('دعم') || v.includes('تحليل') || v.includes('تقييم الأصول') || v.includes('دراسات')) return 'um-badge um-badge--prepare';
  if (v.includes('مراجعة') || v.includes('دراسة')) return 'um-badge um-badge--review';
  if (v.includes('توصية') || v.includes('ترشيح')) return 'um-badge um-badge--recommend';
  if (v.includes('إشراف') || v.includes('متابعة') || v.includes('رقابة') || v.includes('توجيه') || v.includes('معالجة') || v.includes('قيادة')) return 'um-badge um-badge--oversee';
  if (v.includes('اطلاع') || v.includes('إطلاع')) return 'um-badge um-badge--view';
  return 'um-badge';
}

const EMPTY_ROW = { topic: '', exec: '', ceo: '', committee: '', board: '', notes: '' };

export default function UnifiedMatrixPage() {
  const { direction } = useLanguage();
  const t = useT();
  const [tab, setTab] = useState('matrix');
  const [search, setSearch] = useState('');
  const [axisFilter, setAxisFilter] = useState('all');
  const [content, setContent] = useState(defaultContent);
  const [editing, setEditing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [banner, setBanner] = useState(null); // { type: 'ok' | 'error', text }

  const accessDenied = !isSecretaryContextUser();
  const canEdit = hasActionPermission('unified_matrix', 'edit');

  useEffect(() => {
    if (!getAccessToken() || accessDenied) { setLoading(false); return; }
    let cancelled = false;
    (async () => {
      try {
        const saved = await fetchUnifiedMatrix();
        if (!cancelled && saved) setContent({ ...defaultContent(), ...saved });
      } catch {
        /* تبقى البيانات الافتراضية */
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [accessDenied]);

  /* ───── helpers للتعديل ───── */
  const upd = (fn) => setContent((prev) => {
    const next = JSON.parse(JSON.stringify(prev));
    fn(next);
    return next;
  });

  const setRowField = (ai, ri, field, value) => upd((c) => { c.axes[ai].rows[ri][field] = value; });
  const addRow = (ai) => upd((c) => { c.axes[ai].rows.push({ ...EMPTY_ROW }); });
  const removeRow = (ai, ri) => upd((c) => { c.axes[ai].rows.splice(ri, 1); });
  const setAxisTitle = (ai, value) => upd((c) => { c.axes[ai].title = value; });
  const addAxis = () => upd((c) => {
    c.axes.push({ id: `axis_${Date.now()}`, title: 'محور جديد', icon: AXIS_ICONS[c.axes.length % AXIS_ICONS.length], rows: [{ ...EMPTY_ROW }] });
  });
  const removeAxis = (ai) => upd((c) => { c.axes.splice(ai, 1); });

  const setItemField = (listKey, i, field, value) => upd((c) => { c[listKey][i][field] = value; });
  const addItem = (listKey, item) => upd((c) => { c[listKey].push(item); });
  const removeItem = (listKey, i) => upd((c) => { c[listKey].splice(i, 1); });

  const handleSave = async () => {
    setSaving(true);
    setBanner(null);
    try {
      await saveUnifiedMatrix(content);
      setEditing(false);
      setBanner({ type: 'ok', text: 'تم حفظ المصفوفة بنجاح' });
    } catch (e) {
      setBanner({ type: 'error', text: e instanceof Error ? e.message : 'تعذر الحفظ' });
    } finally {
      setSaving(false);
    }
  };

  const handleCancelEdit = async () => {
    setEditing(false);
    setLoading(true);
    try {
      const saved = await fetchUnifiedMatrix();
      setContent(saved ? { ...defaultContent(), ...saved } : defaultContent());
    } catch {
      setContent(defaultContent());
    } finally {
      setLoading(false);
    }
  };

  const handleRestoreDefault = async () => {
    setSaving(true);
    setBanner(null);
    try {
      await resetUnifiedMatrix();
      setContent(defaultContent());
      setEditing(false);
      setBanner({ type: 'ok', text: 'تمت استعادة المصفوفة الافتراضية' });
    } catch (e) {
      setBanner({ type: 'error', text: e instanceof Error ? e.message : 'تعذرت الاستعادة' });
    } finally {
      setSaving(false);
    }
  };

  /* ───── العرض ───── */
  const visibleAxes = useMemo(() => {
    if (editing) return content.axes.map((axis, ai) => ({ axis, ai, rows: axis.rows.map((r, ri) => ({ r, ri })) }));
    const q = search.trim().toLowerCase();
    return content.axes
      .map((axis, ai) => ({ axis, ai, rows: axis.rows.map((r, ri) => ({ r, ri })) }))
      .filter(({ axis }) => axisFilter === 'all' || axis.id === axisFilter)
      .map((x) => ({
        ...x,
        rows: x.rows.filter(({ r }) => !q || ['topic', 'exec', 'ceo', 'committee', 'board', 'notes'].some((k) => (r[k] || '').toLowerCase().includes(q))),
      }))
      .filter((x) => x.rows.length > 0);
  }, [content.axes, search, axisFilter, editing]);

  const totalTopics = content.axes.reduce((s, a) => s + a.rows.length, 0);

  if (accessDenied) return <p style={{ padding: 24, color: '#78909c', fontFamily: 'Cairo, sans-serif' }} dir={direction}>{t('common.noPerm')}</p>;

  return (
    <div className="page" dir={direction}>
      <div className="list-page-card list-page-card--unified um-page">
        <header className="list-page-card__header">
          <div className="list-page-card__titles">
            <span className="list-page-card__title"><Icon name="report" size={18} color="#00334a" style={{ verticalAlign: 'middle', marginLeft: 6 }} />المصفوفة الموحدة للصلاحيات</span>
            <span className="list-page-card__sub">توزيع الصلاحيات بين مجلس الإدارة، اللجان المنبثقة، الرئيس التنفيذي، والإدارة التنفيذية — وثيقة حوكمة مؤسسية موحدة</span>
          </div>
          <div className="list-page-card__actions um-no-print">
            {!editing ? (
              <>
                {canEdit && (
                  <button type="button" className="btn btn--ghost um-btn-outline" onClick={() => { setEditing(true); setBanner(null); }}>
                    <Icon name="edit" size={14} /> تعديل
                  </button>
                )}
                <button type="button" className="btn btn--primary" onClick={() => window.print()}>
                  <Icon name="report" size={14} color="#fff" /> طباعة / PDF
                </button>
              </>
            ) : (
              <>
                <button type="button" className="btn btn--ghost um-btn-outline" onClick={handleRestoreDefault} disabled={saving}>استعادة الافتراضي</button>
                <button type="button" className="btn btn--ghost um-btn-outline" onClick={handleCancelEdit} disabled={saving}>{t('common.cancel')}</button>
                <button type="button" className="btn btn--primary" onClick={handleSave} disabled={saving}>{saving ? '…' : t('common.save')}</button>
              </>
            )}
          </div>
        </header>

        {banner && (
          <div className={`list-page-card__banner ${banner.type === 'error' ? 'list-page-card__banner--error' : ''}`} style={banner.type === 'ok' ? { background: '#e8f5e9', color: '#2e7d32' } : undefined}>
            {banner.text}
          </div>
        )}
        {loading && <div className="list-page-card__loading">جارٍ تحميل المصفوفة…</div>}

        {!loading && (
          <>
            {/* Tabs */}
            <div className="um-tabs um-no-print">
              {TABS.map((x) => (
                <button
                  key={x.id}
                  type="button"
                  className={`um-tab${tab === x.id ? ' um-tab--active' : ''}`}
                  onClick={() => setTab(x.id)}
                >
                  <Icon name={x.icon} size={14} color={tab === x.id ? '#fff' : '#546e7a'} /> {x.label}
                </button>
              ))}
            </div>

            {/* ───────── المصفوفة ───────── */}
            {tab === 'matrix' && (
              <>
                {!editing && (
                  <>
                    <div className="page__stats um-no-print">
                      <div className="page__stat-chip page__stat-chip--total"><Icon name="report" size={13} /> المحاور: {content.axes.length}</div>
                      <div className="page__stat-chip page__stat-chip--active"><Icon name="check-circle" size={13} /> المواضيع: {totalTopics}</div>
                    </div>

                    <div className="page__filters um-no-print">
                      <div className="page__search">
                        <Icon name="search" size={14} color="#90a4ae" className="page__search-icon" />
                        <input className="page__search-input" placeholder="بحث في المواضيع والصلاحيات..." value={search} onChange={(e) => setSearch(e.target.value)} />
                      </div>
                      <select className="page__filter-select" value={axisFilter} onChange={(e) => setAxisFilter(e.target.value)}>
                        <option value="all">جميع المحاور</option>
                        {content.axes.map((a) => <option key={a.id} value={a.id}>{a.title}</option>)}
                      </select>
                    </div>

                    <div className="um-legend um-no-print">
                      <span className="um-badge um-badge--prepare">إعداد / تنفيذ</span>
                      <span className="um-badge um-badge--review">مراجعة</span>
                      <span className="um-badge um-badge--recommend">توصية</span>
                      <span className="um-badge um-badge--approve">اعتماد</span>
                      <span className="um-badge um-badge--oversee">إشراف / متابعة</span>
                      <span className="um-badge um-badge--view">اطلاع</span>
                    </div>
                  </>
                )}

                {visibleAxes.length === 0 && (
                  <div className="empty-state"><div className="empty-state__title">لا توجد نتائج مطابقة</div></div>
                )}

                {visibleAxes.map(({ axis, ai, rows }) => (
                  <div key={axis.id} className="um-axis">
                    <div className="um-axis__title">
                      <Icon name={axis.icon} size={15} color="#fff" />
                      {editing
                        ? <input className="um-edit-input um-edit-input--title" value={axis.title} onChange={(e) => setAxisTitle(ai, e.target.value)} />
                        : axis.title}
                      {editing && (
                        <button type="button" className="um-mini-btn um-mini-btn--del" title="حذف المحور" onClick={() => removeAxis(ai)}>✕</button>
                      )}
                    </div>
                    <div className="table-card">
                      <table className="data-table um-table">
                        <thead>
                          <tr>
                            <th style={{ width: 42 }}>#</th>
                            <th>الموضوع</th>
                            <th>الإدارة التنفيذية</th>
                            <th>الرئيس التنفيذي</th>
                            <th>اللجنة المختصة</th>
                            <th>مجلس الإدارة</th>
                            <th>ملاحظات حوكمية</th>
                            {editing && <th style={{ width: 50 }} />}
                          </tr>
                        </thead>
                        <tbody>
                          {rows.map(({ r, ri }, idx) => (
                            <tr key={ri}>
                              <td className="td-num">{idx + 1}</td>
                              {editing ? (
                                <>
                                  <td><textarea className="um-edit-area" rows={2} value={r.topic} onChange={(e) => setRowField(ai, ri, 'topic', e.target.value)} /></td>
                                  <td><textarea className="um-edit-area" rows={2} value={r.exec} onChange={(e) => setRowField(ai, ri, 'exec', e.target.value)} /></td>
                                  <td><textarea className="um-edit-area" rows={2} value={r.ceo} onChange={(e) => setRowField(ai, ri, 'ceo', e.target.value)} /></td>
                                  <td><textarea className="um-edit-area" rows={2} value={r.committee} onChange={(e) => setRowField(ai, ri, 'committee', e.target.value)} /></td>
                                  <td><textarea className="um-edit-area" rows={2} value={r.board} onChange={(e) => setRowField(ai, ri, 'board', e.target.value)} /></td>
                                  <td><textarea className="um-edit-area" rows={2} value={r.notes} onChange={(e) => setRowField(ai, ri, 'notes', e.target.value)} /></td>
                                  <td>
                                    <button type="button" className="action-btn action-btn--delete" title="حذف الموضوع" onClick={() => removeRow(ai, ri)}><Icon name="trash" size={14} /></button>
                                  </td>
                                </>
                              ) : (
                                <>
                                  <td><strong style={{ color: '#00334a' }}>{r.topic}</strong></td>
                                  <td><span className={roleBadgeClass(r.exec)}>{r.exec}</span></td>
                                  <td><span className={roleBadgeClass(r.ceo)}>{r.ceo}</span></td>
                                  <td><span className={roleBadgeClass(r.committee)}>{r.committee}</span></td>
                                  <td><span className={roleBadgeClass(r.board)}>{r.board}</span></td>
                                  <td className="um-notes">{r.notes}</td>
                                </>
                              )}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                      {editing && (
                        <button type="button" className="um-add-btn" onClick={() => addRow(ai)}><Icon name="plus" size={13} /> إضافة موضوع</button>
                      )}
                    </div>
                  </div>
                ))}

                {editing && (
                  <button type="button" className="um-add-btn um-add-btn--block" onClick={addAxis}><Icon name="plus" size={14} /> إضافة محور جديد</button>
                )}
              </>
            )}

            {/* ───────── اللجان ───────── */}
            {tab === 'committees' && (
              <>
                <p className="um-intro">تتمتع اللجان المنبثقة من مجلس الإدارة بالصلاحيات والأدوار الموضحة أدناه كأذرع مساندة للمجلس:</p>
                <div className="um-committees">
                  {content.committees.map((c, i) => (
                    <div key={i} className="um-committee-card">
                      <div className="um-committee-card__name">
                        <Icon name="board" size={15} color="#fff" />
                        {editing
                          ? <input className="um-edit-input um-edit-input--title" value={c.name} onChange={(e) => setItemField('committees', i, 'name', e.target.value)} />
                          : c.name}
                        {editing && <button type="button" className="um-mini-btn um-mini-btn--del" title="حذف اللجنة" onClick={() => removeItem('committees', i)}>✕</button>}
                      </div>
                      <div className="um-committee-card__goal">
                        {editing
                          ? <textarea className="um-edit-area" rows={2} value={c.goal} onChange={(e) => setItemField('committees', i, 'goal', e.target.value)} />
                          : c.goal}
                      </div>
                      <div className="um-committee-card__grid">
                        {[
                          ['approve', 'صلاحيات الاعتماد', 'um-badge--approve'],
                          ['review', 'صلاحيات المراجعة', 'um-badge--review'],
                          ['recommend', 'صلاحيات التوصية', 'um-badge--recommend'],
                          ['monitor', 'المتابعة / الرقابة', 'um-badge--oversee'],
                        ].map(([field, label, cls]) => (
                          <div key={field}>
                            <span className={`um-badge ${cls}`}>{label}</span>
                            {editing
                              ? <textarea className="um-edit-area" rows={3} value={c[field]} onChange={(e) => setItemField('committees', i, field, e.target.value)} style={{ marginTop: 8 }} />
                              : <p>{c[field]}</p>}
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
                {editing && (
                  <button type="button" className="um-add-btn um-add-btn--block" onClick={() => addItem('committees', { name: 'لجنة جديدة', goal: '', approve: '', review: '', recommend: '', monitor: '' })}>
                    <Icon name="plus" size={14} /> إضافة لجنة
                  </button>
                )}
              </>
            )}

            {/* ───────── المبادئ والمستويات ───────── */}
            {tab === 'principles' && (
              <>
                <h3 className="um-section-title">المبادئ الحاكمة</h3>
                <div className="um-principles">
                  {content.governingPrinciples.map((p, i) => (
                    <div key={i} className="um-principle">
                      <div className="um-principle__icon"><Icon name={p.icon || 'check-circle'} size={18} color="#00334a" /></div>
                      <div style={{ flex: 1 }}>
                        {editing ? (
                          <>
                            <input className="um-edit-input" value={p.title} onChange={(e) => setItemField('governingPrinciples', i, 'title', e.target.value)} />
                            <textarea className="um-edit-area" rows={3} value={p.desc} onChange={(e) => setItemField('governingPrinciples', i, 'desc', e.target.value)} style={{ marginTop: 6 }} />
                            <button type="button" className="um-mini-btn um-mini-btn--del-dark" onClick={() => removeItem('governingPrinciples', i)}>حذف</button>
                          </>
                        ) : (
                          <>
                            <div className="um-principle__title">{p.title}</div>
                            <div className="um-principle__desc">{p.desc}</div>
                          </>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
                {editing && (
                  <button type="button" className="um-add-btn" onClick={() => addItem('governingPrinciples', { title: '', desc: '', icon: 'check-circle' })}><Icon name="plus" size={13} /> إضافة مبدأ</button>
                )}

                <h3 className="um-section-title">المستويات الحوكمية</h3>
                <div className="table-card">
                  <table className="data-table um-table">
                    <thead>
                      <tr>
                        <th style={{ width: 42 }}>#</th>
                        <th>المستوى الحوكمي</th>
                        <th>الوصف العام والمسؤولية</th>
                        {editing && <th style={{ width: 50 }} />}
                      </tr>
                    </thead>
                    <tbody>
                      {content.governanceLevels.map((l, i) => (
                        <tr key={i}>
                          <td className="td-num">{i + 1}</td>
                          {editing ? (
                            <>
                              <td><textarea className="um-edit-area" rows={2} value={l.level} onChange={(e) => setItemField('governanceLevels', i, 'level', e.target.value)} /></td>
                              <td><textarea className="um-edit-area" rows={2} value={l.desc} onChange={(e) => setItemField('governanceLevels', i, 'desc', e.target.value)} /></td>
                              <td><button type="button" className="action-btn action-btn--delete" onClick={() => removeItem('governanceLevels', i)}><Icon name="trash" size={14} /></button></td>
                            </>
                          ) : (
                            <>
                              <td><strong style={{ color: '#00334a' }}>{l.level}</strong></td>
                              <td className="um-wrap">{l.desc}</td>
                            </>
                          )}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {editing && (
                    <button type="button" className="um-add-btn" onClick={() => addItem('governanceLevels', { level: '', desc: '' })}><Icon name="plus" size={13} /> إضافة مستوى</button>
                  )}
                </div>

                <h3 className="um-section-title">تعريف مختصر للأدوار</h3>
                <div className="um-roles">
                  {content.roleDefinitions.map((r, i) => (
                    <div key={i} className="um-role">
                      {editing ? (
                        <>
                          <input className="um-edit-input" value={r.role} onChange={(e) => setItemField('roleDefinitions', i, 'role', e.target.value)} />
                          <textarea className="um-edit-area" rows={4} value={r.desc} onChange={(e) => setItemField('roleDefinitions', i, 'desc', e.target.value)} style={{ marginTop: 6 }} />
                          <button type="button" className="um-mini-btn um-mini-btn--del-dark" onClick={() => removeItem('roleDefinitions', i)}>حذف</button>
                        </>
                      ) : (
                        <>
                          <div className="um-role__name">{r.role}</div>
                          <div className="um-role__desc">{r.desc}</div>
                        </>
                      )}
                    </div>
                  ))}
                </div>
                {editing && (
                  <button type="button" className="um-add-btn" onClick={() => addItem('roleDefinitions', { role: '', desc: '' })}><Icon name="plus" size={13} /> إضافة دور</button>
                )}

                <h3 className="um-section-title">المبادئ المنظمة لمصفوفة الصلاحيات</h3>
                <div className="um-provisions">
                  {content.regulatingPrinciples.map((p, i) => (
                    <div key={i} className="um-provision">
                      <span className="um-provision__num">{i + 1}</span>
                      <div style={{ flex: 1 }}>
                        {editing ? (
                          <>
                            <input className="um-edit-input" value={p.title} onChange={(e) => setItemField('regulatingPrinciples', i, 'title', e.target.value)} />
                            <textarea className="um-edit-area" rows={3} value={p.desc} onChange={(e) => setItemField('regulatingPrinciples', i, 'desc', e.target.value)} style={{ marginTop: 6 }} />
                            <button type="button" className="um-mini-btn um-mini-btn--del-dark" onClick={() => removeItem('regulatingPrinciples', i)}>حذف</button>
                          </>
                        ) : (
                          <>
                            <div className="um-provision__title">{p.title}</div>
                            <div className="um-provision__desc">{p.desc}</div>
                          </>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
                {editing && (
                  <button type="button" className="um-add-btn" onClick={() => addItem('regulatingPrinciples', { title: '', desc: '' })}><Icon name="plus" size={13} /> إضافة مبدأ منظم</button>
                )}
              </>
            )}

            {/* ───────── أحكام تنظيمية ───────── */}
            {tab === 'provisions' && (
              <>
                <div className="um-provisions" style={{ marginTop: 16 }}>
                  {content.generalProvisions.map((p, i) => (
                    <div key={i} className="um-provision">
                      <span className="um-provision__num">{i + 1}</span>
                      <div style={{ flex: 1 }}>
                        {editing ? (
                          <>
                            <input className="um-edit-input" value={p.title} onChange={(e) => setItemField('generalProvisions', i, 'title', e.target.value)} />
                            <textarea className="um-edit-area" rows={3} value={p.desc} onChange={(e) => setItemField('generalProvisions', i, 'desc', e.target.value)} style={{ marginTop: 6 }} />
                            <button type="button" className="um-mini-btn um-mini-btn--del-dark" onClick={() => removeItem('generalProvisions', i)}>حذف</button>
                          </>
                        ) : (
                          <>
                            <div className="um-provision__title">{p.title}</div>
                            <div className="um-provision__desc">{p.desc}</div>
                          </>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
                {editing && (
                  <button type="button" className="um-add-btn" onClick={() => addItem('generalProvisions', { title: '', desc: '' })}><Icon name="plus" size={13} /> إضافة حكم</button>
                )}
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
}
